import { EpubCFI } from "epubjs";
import { debounce } from "../../common/lib/debounce";
import { closestElement } from "../common/lib/nodes";
import EPUBView from "./epub-view";
import { PersistentRange } from "../common/lib/range";
import { isSafari } from "../../common/lib/utilities";
import { getSelectionRanges } from "../common/lib/selection";
import { rectContains } from "../common/lib/rect";
class AbstractFlow {
    _view;
    _cachedStartSection = null;
    _cachedStartRange = null;
    _cachedStartCFI = null;
    _cachedStartCFIOffsetY = null;
    _iframe;
    _iframeWindow;
    _iframeDocument;
    _scale = 1;
    _onUpdateViewState;
    _onUpdateViewStats;
    _onViewUpdate;
    _onPushHistoryPoint;
    _nextHistoryPushIsFromNavigation = false;
    constructor(options) {
        this._view = options.view;
        this._iframe = options.iframe;
        this._iframeWindow = options.iframe.contentWindow;
        this._iframeDocument = options.iframe.contentDocument;
        this._scale = options.view.scale;
        this._onUpdateViewState = options.onUpdateViewState;
        this._onUpdateViewStats = options.onUpdateViewStats;
        this._onViewUpdate = options.onViewUpdate;
        this._onPushHistoryPoint = options.onPushHistoryPoint;
        this._iframeWindow.addEventListener('scroll', this._pushHistoryPoint);
        let intersectionObserver = new IntersectionObserver(() => this.invalidate(), {
            threshold: [0, 1]
        });
        for (let range of this._view.pageMapping.tree.keys()) {
            let elem = closestElement(range.startContainer);
            if (elem) {
                intersectionObserver.observe(elem);
            }
        }
    }
    get startSection() {
        if (!this._cachedStartSection) {
            this.update();
        }
        return this._cachedStartSection;
    }
    get startRange() {
        if (!this._cachedStartRange) {
            this.update();
        }
        return this._cachedStartRange?.toRange() ?? null;
    }
    get startCFI() {
        if (!this._cachedStartCFI) {
            this.update();
        }
        return this._cachedStartCFI;
    }
    get startCFIOffsetY() {
        if (this._cachedStartCFIOffsetY === null) {
            this.update();
        }
        return this._cachedStartCFIOffsetY;
    }
    get startRangeIsBeforeFirstMapping() {
        if (!this.startRange) {
            return true;
        }
        let firstMappedRange = this._view.pageMapping.firstRange;
        if (!firstMappedRange) {
            return false;
        }
        return EPUBView.compareBoundaryPoints(Range.START_TO_START, this.startRange, firstMappedRange) < 0;
    }
    invalidate = debounce(() => {
        this._cachedStartRange = null;
        this._cachedStartCFIOffsetY = null;
        this._cachedStartCFI = null;
        this.update();
        this._onUpdateViewState();
        this._onUpdateViewStats();
        this._pushHistoryPoint();
    }, 200);
    _pushHistoryPoint = () => {
        this._onPushHistoryPoint(!this._nextHistoryPushIsFromNavigation);
        this._nextHistoryPushIsFromNavigation = false;
    };
    setScale(scale) {
        this._scale = scale;
    }
    destroy() {
        this._iframeWindow.removeEventListener('scroll', this._pushHistoryPoint);
    }
}
export class ScrolledFlow extends AbstractFlow {
    static SCROLL_PADDING_UNSCALED = 35;
    constructor(options) {
        super(options);
        this._iframe.classList.add('flow-mode-scrolled');
        this._iframeDocument.body.classList.add('flow-mode-scrolled');
        for (let view of this._view.renderers) {
            view.mount();
        }
        if (isSafari) {
            // Safari doesn't actually make the body scrollable unless we invalidate
            // something or other by manually setting overflowY to auto
            // (This is a Safari bug)
            setTimeout(() => {
                this._iframeDocument.body.style.overflowY = 'auto';
            });
        }
    }
    destroy() {
        super.destroy();
        this._iframe.classList.remove('flow-mode-scrolled');
        this._iframeDocument.body.classList.remove('flow-mode-scrolled');
        if (isSafari) {
            // Undo our Safari workaround above
            this._iframeDocument.body.style.overflowY = '';
        }
    }
    scrollIntoView(target, options) {
        let rect = (target instanceof PersistentRange ? target.toRange() : target).getBoundingClientRect();
        if (options?.ifNeeded && (rect.top >= 0 && rect.bottom < this._iframe.clientHeight)) {
            return;
        }
        if (!options?.skipHistory) {
            this._nextHistoryPushIsFromNavigation = true;
        }
        // Disable smooth scrolling when target is too far away
        if (options?.behavior == 'smooth'
            && Math.abs(rect.top + rect.bottom / 2) > this._iframe.clientHeight * 2) {
            options.behavior = 'auto';
        }
        if ('nodeType' in target) {
            target.scrollIntoView(options);
            this.invalidate();
            return;
        }
        let x = rect.x + rect.width / 2;
        let y = rect.y;
        if (options && options.block == 'center') {
            y += rect.height / 2;
            y -= this._iframe.clientHeight / 2;
        }
        if (options && options.offsetY !== undefined) {
            y -= options.offsetY;
        }
        this._iframeWindow.scrollBy({
            ...options,
            left: x,
            top: y,
        });
        this.invalidate();
    }
    get scrollPadding() {
        return ScrolledFlow.SCROLL_PADDING_UNSCALED * this._scale;
    }
    canNavigateToPreviousPage() {
        return this._iframeWindow.scrollY >= this._iframe.clientHeight
            - this.scrollPadding;
    }
    canNavigateToNextPage() {
        return this._iframeWindow.scrollY <= this._iframeDocument.documentElement.scrollHeight
            - this._iframe.clientHeight * 2
            + this.scrollPadding;
    }
    navigateToPreviousPage() {
        if (!this.canNavigateToPreviousPage()) {
            return;
        }
        this._iframeWindow.scrollBy({ top: -this._iframe.clientHeight + this.scrollPadding });
        this._onViewUpdate();
    }
    navigateToNextPage() {
        if (!this.canNavigateToNextPage()) {
            return;
        }
        this._iframeWindow.scrollBy({ top: this._iframe.clientHeight - this.scrollPadding });
        this._onViewUpdate();
    }
    navigateToFirstPage() {
        this._iframeWindow.scrollTo({ top: 0 });
        this._onViewUpdate();
    }
    navigateToLastPage() {
        this._iframeWindow.scrollTo({ top: this._iframeDocument.documentElement.scrollHeight });
        this._onViewUpdate();
    }
    update() {
        let foundStart = false;
        for (let renderer of this._view.renderers) {
            if (!renderer.mounted) {
                continue;
            }
            // Avoid calling getBoundingClientRect() because that would force a layout, which is expensive
            let visible = renderer.container.offsetTop < this._iframeWindow.scrollY + this._iframe.clientHeight
                && renderer.container.offsetTop + renderer.container.offsetHeight >= this._iframeWindow.scrollY;
            if (!foundStart) {
                if (!visible) {
                    continue;
                }
                this._cachedStartSection = renderer.section;
                let startRange = renderer.getFirstVisibleRange(false, false);
                let startCFIRange = renderer.getFirstVisibleRange(false, true);
                if (startRange) {
                    // Navigating to page N might put us on a line containing the boundary between page N-1 and page N
                    // somewhere in its middle. We want the page number field to show N in that case, not N-1.
                    // We collapse the range to its end so that, for the purpose of comparing with page
                    // number-delineating ranges, it looks like we're scrolled down a little further than we actually
                    // are - to the end of the uppermost element or text node.
                    // TODO: Make sure this doesn't break anything involving images / block elements / long text
                    startRange.collapse(false);
                    this._cachedStartRange = new PersistentRange(startRange);
                }
                if (startCFIRange) {
                    // But CFIs should be calculated based on the start of the range, so collapse to the start
                    startCFIRange.collapse(true);
                    this._cachedStartCFI = new EpubCFI(startCFIRange, renderer.section.cfiBase);
                    this._cachedStartCFIOffsetY = startCFIRange.getBoundingClientRect().top;
                }
                if (startRange && startCFIRange) {
                    foundStart = true;
                }
            }
            else if (!visible) {
                break;
            }
        }
    }
    setSpreadMode() {
        // No-op
    }
}
const PAGE_TURN_SWIPE_LENGTH_PX = 100;
const PAGE_TURN_TAP_MARGIN_PX = 150;
const EPSILON_PX = 10;
export class PaginatedFlow extends AbstractFlow {
    _sectionsContainer;
    _swipeIndicators;
    _touchStartID = null;
    _touchStartX = 0;
    _touchStartY = 0;
    _currentSectionIndex;
    constructor(options) {
        super(options);
        this._sectionsContainer = this._iframeDocument.body.querySelector(':scope > .sections');
        this._swipeIndicators = this._iframeDocument.querySelector('.swipe-indicators');
        this._iframeDocument.addEventListener('keydown', this._handleKeyDown, { capture: true });
        this._iframeDocument.addEventListener('pointerdown', this._handlePointerDown);
        this._iframeDocument.addEventListener('pointermove', this._handlePointerMove);
        this._iframeDocument.addEventListener('pointerup', this._handlePointerUp);
        this._iframeDocument.addEventListener('wheel', this._handleWheel, { passive: false });
        this._iframeDocument.addEventListener('selectionchange', this._handleSelectionChange);
        this._iframe.classList.add('flow-mode-paginated');
        this._iframeDocument.body.classList.add('flow-mode-paginated');
    }
    destroy() {
        super.destroy();
        this._iframeDocument.removeEventListener('keydown', this._handleKeyDown, { capture: true });
        this._iframeDocument.removeEventListener('pointerdown', this._handlePointerDown);
        this._iframeDocument.removeEventListener('pointermove', this._handlePointerMove);
        this._iframeDocument.removeEventListener('pointerup', this._handlePointerUp);
        this._iframeDocument.removeEventListener('wheel', this._handleWheel);
        this._iframeDocument.removeEventListener('selectionchange', this._handleSelectionChange);
        this._iframe.classList.remove('flow-mode-paginated');
        this._iframeDocument.body.classList.remove('flow-mode-paginated');
    }
    get _spreadWidth() {
        return this._sectionsContainer.offsetWidth
            + parseFloat(getComputedStyle(this._sectionsContainer).columnGap);
    }
    get currentSectionIndex() {
        return this._currentSectionIndex;
    }
    set currentSectionIndex(index) {
        if (index === this._currentSectionIndex) {
            return;
        }
        let oldIndex = this._currentSectionIndex;
        this._currentSectionIndex = index;
        this._sectionsContainer.scrollTo({ left: 0, top: 0 });
        if (oldIndex === undefined) {
            for (let view of this._view.renderers) {
                view.unmount();
            }
        }
        else {
            this._view.renderers[oldIndex].unmount();
        }
        let view = this._view.renderers[index];
        view.mount();
        this._onViewUpdate();
    }
    scrollIntoView(target, options) {
        let index = EPUBView.getContainingSectionIndex(target);
        if (index === null) {
            return;
        }
        if (!options?.skipHistory) {
            this._nextHistoryPushIsFromNavigation = true;
        }
        this.currentSectionIndex = index;
        // Otherwise, center the target horizontally
        let rect = (target instanceof PersistentRange ? target.toRange() : target).getBoundingClientRect();
        let x = rect.x + this._sectionsContainer.scrollLeft;
        if (options?.block === 'center') {
            x += rect.width / 2;
        }
        let spreadWidth = this._spreadWidth;
        this._sectionsContainer.scrollTo({
            left: Math.floor(x / spreadWidth) * spreadWidth,
            top: 0
        });
        this._onViewUpdate();
    }
    canNavigateToPreviousPage() {
        if (this.canNavigateToPreviousSection()) {
            return true;
        }
        return this._sectionsContainer.scrollLeft > 0;
    }
    canNavigateToNextPage() {
        if (this.canNavigateToNextSection()) {
            return true;
        }
        return this._sectionsContainer.scrollLeft < this._sectionsContainer.scrollWidth - this._sectionsContainer.offsetWidth;
    }
    atStartOfSection() {
        return this._sectionsContainer.scrollLeft == 0;
    }
    atEndOfSection() {
        return this._sectionsContainer.scrollLeft == this._sectionsContainer.scrollWidth - this._sectionsContainer.offsetWidth;
    }
    canNavigateToPreviousSection() {
        return this.currentSectionIndex > 0;
    }
    canNavigateToNextSection() {
        return this.currentSectionIndex < this._view.renderers.length - 1;
    }
    navigateToPreviousSection() {
        if (this.canNavigateToPreviousSection()) {
            this.currentSectionIndex--;
        }
    }
    navigateToNextSection() {
        if (this.canNavigateToNextSection()) {
            this.currentSectionIndex++;
        }
    }
    navigateToPreviousPage() {
        if (!this.canNavigateToPreviousPage()) {
            return;
        }
        if (this.atStartOfSection()) {
            this.navigateToPreviousSection();
            this._sectionsContainer.scrollTo({ left: this._sectionsContainer.scrollWidth, top: 0 });
            this._onViewUpdate();
            return;
        }
        this._sectionsContainer.scrollBy({
            left: -this._spreadWidth,
            behavior: 'auto' // TODO 'smooth' once annotation positioning is fixed
        });
        this._onViewUpdate();
    }
    navigateToNextPage() {
        if (!this.canNavigateToNextPage()) {
            return;
        }
        if (this.atEndOfSection()) {
            this.navigateToNextSection();
            return;
        }
        this._sectionsContainer.scrollBy({
            left: this._spreadWidth,
            behavior: 'auto' // TODO 'smooth' once annotation positioning is fixed
        });
        this._onViewUpdate();
    }
    navigateLeft() {
        if (this._view.pageProgressionRTL) {
            this.navigateToNextPage();
        }
        else {
            this.navigateToPreviousPage();
        }
    }
    navigateRight() {
        if (this._view.pageProgressionRTL) {
            this.navigateToPreviousPage();
        }
        else {
            this.navigateToNextPage();
        }
    }
    navigateToFirstPage() {
        this.currentSectionIndex = this._view.renderers[0].section.index;
        this._sectionsContainer.scrollTo({ left: 0, top: 0 });
        this._onViewUpdate();
    }
    navigateToLastPage() {
        this.currentSectionIndex = this._view.renderers[this._view.renderers.length - 1].section.index;
        this._sectionsContainer.scrollTo({ left: this._sectionsContainer.scrollWidth, top: 0 });
        this._onViewUpdate();
    }
    _handleKeyDown = (event) => {
        let { key, shiftKey } = event;
        // Left/right arrows are handled in EPUBView
        if (!shiftKey) {
            if (key == 'ArrowUp') {
                this.navigateToPreviousPage();
                event.preventDefault();
                return;
            }
            if (key == 'ArrowDown') {
                this.navigateToNextPage();
                event.preventDefault();
                return;
            }
            if (key == 'PageUp') {
                this.navigateToPreviousPage();
                event.preventDefault();
                return;
            }
            if (key == 'PageDown') {
                this.navigateToNextPage();
                event.preventDefault();
                return;
            }
            if (key == 'Home') {
                this.navigateToFirstPage();
                event.preventDefault();
                return;
            }
            if (key == 'End') {
                this.navigateToLastPage();
                event.preventDefault();
                return;
            }
        }
        if (key == ' ') {
            if (shiftKey) {
                this.navigateToPreviousPage();
            }
            else {
                this.navigateToNextPage();
            }
            event.preventDefault();
        }
    };
    _handlePointerDown = (event) => {
        if (this._touchStartID !== null
            || (event.pointerType !== 'touch' && event.pointerType !== 'pen')
            || event.target.closest('#annotation-overlay')) {
            return;
        }
        // Safari: Ignore touches near a selection, because Safari still sends pointer events
        // for selection handle drags
        if (isSafari) {
            let selectionRect = getSelectionRanges(this._iframeWindow.getSelection())[0]
                ?.getBoundingClientRect();
            if (selectionRect && selectionRect.width && selectionRect.height) {
                selectionRect.x -= 40;
                selectionRect.y -= 40;
                selectionRect.width += 80;
                selectionRect.height += 80;
                if (rectContains(selectionRect, event.clientX, event.clientY)) {
                    console.log('Ignoring pointerdown near selection');
                    return;
                }
            }
        }
        this._touchStartID = event.pointerId;
        this._touchStartX = event.clientX;
        this._touchStartY = event.clientY;
    };
    _handlePointerMove = (event) => {
        if (this._touchStartID === null || event.pointerId !== this._touchStartID) {
            return;
        }
        let swipeAmount = (event.clientX - this._touchStartX) / PAGE_TURN_SWIPE_LENGTH_PX;
        // If on the first/last page, clamp the CSS variable so the indicator doesn't expand all the way
        if (swipeAmount < 0 && !this.canNavigateToNextPage()) {
            swipeAmount = Math.max(swipeAmount, -0.6);
        }
        else if (swipeAmount > 0 && !this.canNavigateToPreviousPage()) {
            swipeAmount = Math.min(swipeAmount, 0.6);
        }
        this._swipeIndicators.style.setProperty('--swipe-amount', swipeAmount.toString());
    };
    _handlePointerUp = (event) => {
        if (this._touchStartID === null || event.pointerId !== this._touchStartID) {
            return;
        }
        event.preventDefault();
        this._swipeIndicators.style.setProperty('--swipe-amount', '0');
        this._touchStartID = null;
        // Switch pages after swiping
        let swipeAmount = (event.clientX - this._touchStartX) / PAGE_TURN_SWIPE_LENGTH_PX;
        if (swipeAmount <= -1) {
            this.navigateRight();
        }
        else if (swipeAmount >= 1) {
            this.navigateLeft();
        }
        // If there's no selection, allow single-tap page turns
        else if (this._iframeWindow.getSelection().isCollapsed
            && !this._view.selectedAnnotationIDs.length
            && Math.abs(event.clientX - this._touchStartX) < EPSILON_PX
            && Math.abs(event.clientY - this._touchStartY) < EPSILON_PX) {
            if (event.clientX >= this._iframeWindow.innerWidth - PAGE_TURN_TAP_MARGIN_PX) {
                this.navigateRight();
            }
            else if (event.clientX <= PAGE_TURN_TAP_MARGIN_PX) {
                this.navigateLeft();
            }
        }
    };
    _handleWheel = debounce((event) => {
        let tableParent = event.target.closest('table, .table-like');
        if (tableParent && tableParent.clientHeight < tableParent.scrollHeight) {
            return;
        }
        if (event.deltaY < 0) {
            this.navigateToPreviousPage();
            event.preventDefault();
        }
        else if (event.deltaY > 0) {
            this.navigateToNextPage();
            event.preventDefault();
        }
    }, 100);
    _handleSelectionChange = () => {
        this._swipeIndicators.style.setProperty('--swipe-amount', '0');
        this._touchStartID = null;
    };
    update() {
        let foundStart = false;
        for (let renderer of this._view.renderers.values()) {
            if (!renderer.mounted) {
                continue;
            }
            // Avoid calling getBoundingClientRect() because that would force a layout, which is expensive
            let visible = renderer.container.offsetLeft < this._iframeWindow.scrollX + this._iframe.clientWidth
                && renderer.container.offsetLeft + renderer.container.offsetWidth >= this._iframeWindow.scrollX;
            if (!foundStart) {
                if (!visible) {
                    continue;
                }
                this._cachedStartSection = renderer.section;
                let startRange = renderer.getFirstVisibleRange(true, false);
                let startCFIRange = renderer.getFirstVisibleRange(true, true);
                if (startRange) {
                    startRange.collapse(false);
                    this._cachedStartRange = new PersistentRange(startRange);
                }
                if (startCFIRange) {
                    startCFIRange.collapse(false);
                    this._cachedStartCFI = new EpubCFI(startCFIRange, renderer.section.cfiBase);
                    this._cachedStartCFIOffsetY = 0;
                }
                if (startRange && startCFIRange) {
                    foundStart = true;
                }
            }
            else if (!visible) {
                break;
            }
        }
    }
    setSpreadMode(spreadMode) {
        this._sectionsContainer.classList.toggle('spread-mode-none', spreadMode === 0 /* SpreadMode.None */);
        this._sectionsContainer.classList.toggle('spread-mode-odd', spreadMode === 1 /* SpreadMode.Odd */);
    }
}
